import { TAppDispatch, TRootState } from 'store/types'
import ttsSlice from 'store/features/tts/slice'

import { getUserTtsCredits_API } from 'api'
import {
  generateTtsItems_API,
  getTtsLanguageOptions_API,
  getTtsSpeakers_API,
  getTtsSpeakerTones_API
} from 'api/tts'
import { ErrorKeys } from 'consts/errorKeys'
import { getFinalReorderedAndFilteredItems } from 'utils/tts'
import {
  IGenerateTtsItemsRequestBody,
  ITtsItem,
  IUpdatedTtsItemData,
  ITtsSpeaker
} from 'types/text-to-speech'
import { ttsSelectors } from 'store/features/tts/selectors'
import { userSelectors } from 'store/features/user/selectors'
import {
  determineShouldReSync,
  fetchTtsItemsByIds,
  getDraftTtsBlock,
  getDraftTtsItem,
  getFirstSpeakerIdForUser,
  getItemsToFetch,
  mergeAllItems
} from './helpers'
import {
  TTS_BULK_DEFAULT_SPEED,
  TTS_DEFAULT_BULK_INSTRUCTION,
  TTS_GENERATION_STATUS,
  TTS_ITEM_DATA_CHANGEABLE_KEYS
} from 'consts/tts'
import { getIsTextChanged } from 'containers/SubSidebar/TtsContent/components/TextArea/utils'
import { ITtsProcessResult } from 'store/types/tts'
import { captureException } from '@sentry/react'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { withAppThunks } from 'store/with-app-thunks'
import { handleApiError } from 'store/utils'

const { actions } = ttsSlice

const addNewTtsDraftBlock =
  (id?: number) => (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const { speakerId, speed, langCode, instructions } =
      ttsSelectors.newTtsItemProps(state)

    const draftTtsItem = getDraftTtsBlock({
      id,
      speakerId,
      speed,
      langCode,
      instructions
    })

    dispatch(actions.addNewTtsBlock(draftTtsItem))
  }

const setTtsInitialDraftItemData =
  () => (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const { speakerId, speed, langCode, instructions } =
      ttsSelectors.newTtsItemProps(state)

    const draftTtsItem = getDraftTtsItem(
      speakerId,
      speed,
      langCode,
      instructions
    )

    dispatch(actions.setTtsItems(draftTtsItem))
  }

const setLoadingStates = (isLoading: boolean) => (dispatch: TAppDispatch) => {
  dispatch(actions.setTtsItemsIsLoading(isLoading))
  dispatch(actions.setInitialTtsItemsIsLoading(isLoading))
}

const getLanguageOptions =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const langOptions = ttsSelectors.languageOptionsData(getState())
    const isLoading = ttsSelectors.isLanguageOptionsLoading(getState())
    if (langOptions?.length || isLoading) {
      return
    }

    dispatch(actions.setLanguageOptionsIsLoading(true))
    try {
      const response = await getTtsLanguageOptions_API()
      dispatch(actions.setLanguageOptions(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setLanguageOptionsIsLoading(false))
    }
  }

const getUnchangedItemsAndCallCallback = (
  state: TRootState,
  speakers: ITtsSpeaker[],
  langCode: string,
  callback?: (speakerId: number, unchangedItems: number[]) => void
) => {
  if (!speakers?.length) {
    return
  }

  const isFreeUser = userSelectors.isFreeUser(state)
  const firstSpeakerId = getFirstSpeakerIdForUser(speakers, isFreeUser)

  const unchangedItems = ttsSelectors.ttsUnchangedItems(state, {
    speakerId: firstSpeakerId,
    speed: TTS_BULK_DEFAULT_SPEED,
    langCode,
    instructions: TTS_DEFAULT_BULK_INSTRUCTION
  })

  callback?.(firstSpeakerId, unchangedItems)
}

const getSpeakersPerLangCode =
  (
    langCode: string,
    callback?: (speakerId: number, unchangedItems: number[]) => void
  ) =>
  async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const speakers = ttsSelectors.speakersPerLangCodeSelector(state, langCode)
    const isLoading = ttsSelectors.speakersPerLangCodeIsLoadingSelector(
      state,
      langCode
    )

    if (isLoading) {
      return
    }

    // If speakers already exist in store, process them and return
    if (speakers?.length) {
      getUnchangedItemsAndCallCallback(state, speakers, langCode, callback)
      return
    }

    dispatch(
      actions.setSpeakersPerLangCodeIsLoading({
        langCode,
        isLoading: true
      })
    )

    try {
      const response = await getTtsSpeakers_API({ langCode })
      dispatch(
        actions.setSpeakersPerLangCode({
          langCode,
          speakers: response.data
        })
      )
      const updatedState = getState()
      getUnchangedItemsAndCallCallback(
        updatedState,
        response.data,
        langCode,
        callback
      )
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(
        actions.setSpeakersPerLangCodeIsLoading({
          langCode,
          isLoading: false
        })
      )
    }
  }

const getSpeakerTones =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const isLoading = ttsSelectors.isSpeakerTonesLoading(getState())
    if (isLoading) {
      return
    }

    dispatch(actions.setSpeakerTonesIsLoading(true))
    try {
      const response = await getTtsSpeakerTones_API()
      dispatch(actions.setSpeakerTones(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setSpeakerTonesIsLoading(false))
    }
  }

const getUserTtsCredits =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const isLoading = ttsSelectors.isTtsCreditsLoading(getState())
    if (isLoading) {
      return
    }

    dispatch(actions.setTtsCreditsIsLoading(true))
    try {
      const response = await getUserTtsCredits_API()
      dispatch(actions.setTtsCredits(response.data.availableCredits))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setTtsCreditsIsLoading(false))
    }
  }

//// *********** generation thunks and funcs *********** ////

const generateSingleTtsItem =
  (item: ITtsItem) =>
  async (dispatch: TAppDispatch): Promise<ITtsProcessResult> => {
    const {
      text,
      speakerId,
      instructions,
      id: prototypeTTsId,
      langCode,
      speed
    } = item.data

    dispatch(actions.updateTtsLoadingState({ id: prototypeTTsId, value: true }))

    const requestBody: IGenerateTtsItemsRequestBody = {
      text,
      langCode,
      speed,
      id: speakerId,
      instructions
    }

    try {
      const response = await generateTtsItems_API(requestBody)
      return {
        success: true,
        data: response.data, // here we will have the id of the tts item that was generated
        originalId: prototypeTTsId
      }
    } catch (error) {
      dispatch(
        actions.updateTtsGeneratedState({
          id: prototypeTTsId,
          value: false
        })
      )
      return {
        success: false,
        originalId: prototypeTTsId,
        errorCode:
          error instanceof Error && error.cause
            ? error.cause &&
              typeof error.cause === 'object' &&
              'code' in error.cause
              ? (error.cause as { code: string }).code
              : ''
            : ''
      }
    } finally {
      dispatch(
        actions.updateTtsLoadingState({ id: prototypeTTsId, value: false })
      )
    }
  }

const processGenerationResults = (results: ITtsProcessResult[]) => {
  const successfulResults = results.filter(result => result.success)
  const failedResults = results.filter(result => !result.success)

  const generatedItems = successfulResults
    .map(result => {
      return {
        ...result.data,
        originalId: result.originalId
      }
    })
    .filter((item): item is IUpdatedTtsItemData => !!item)

  const itemStatusMap = results.map(result => ({
    id: result.originalId,
    status: result.success ? 'success' : 'failed'
  }))

  return {
    successfulResults,
    failedResults,
    generatedItems,
    itemStatusMap
  }
}

const updateStateWithResults = (
  processingResult: ReturnType<typeof processGenerationResults>
) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    const { failedResults, generatedItems, itemStatusMap } = processingResult

    dispatch(actions.setNewGeneratedItemsInitialIdAndStatus(itemStatusMap))
    if (generatedItems.length > 0) {
      dispatch(appThunks.projectDataThunks.updateTtsAudios(generatedItems))
    }

    if (failedResults.length > 0) {
      const errorCode = failedResults[0].errorCode
      dispatch(handleTtsGenerationError(errorCode))
    } else {
      dispatch(actions.setTtsGenerationStatus(TTS_GENERATION_STATUS.ok))
    }
  })

const performCleanup = () => (dispatch: TAppDispatch) => {
  dispatch(getUserTtsCredits())
  dispatch(actions.setTtsItemsIsLoading(false))
  dispatch(actions.deleteAllDraftEmptyTtsItems())
}

// Main function to generate TTS items in parallel //
const generateTtsItems =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const itemsToGenerate = ttsSelectors.notGeneratedAndNotEmptyTtsItems(state)

    if (!itemsToGenerate.length) {
      return
    }

    dispatch(actions.setTtsItemsIsLoading(true))
    dispatch(actions.setTtsGenerationStatus(TTS_GENERATION_STATUS.processing))

    try {
      const generationResults = await Promise.all(
        itemsToGenerate.map(item => dispatch(generateSingleTtsItem(item)))
      )

      const processingResult = processGenerationResults(generationResults)

      dispatch(updateStateWithResults(processingResult))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(performCleanup())
    }
  }

//// *********** the end. *********** ////

/**
 * Synchronizes TTS items between the timeline and local state.
 * Fetches missing items from API, reorders them according to timeline,
 * deletes items that were deleted from the timeline,
 * and merges with pending/draft items while preserving user changes.
 */
export const syncTtsAudios =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    try {
      const state = getState()
      const {
        sortedTtsAudiosIds,
        notGeneratedOrDraftTtsItems,
        previouslyGeneratedUnchangedItems,
        newGeneratedItemsInitialIdAndStatus
      } = ttsSelectors.selectAndPrepareStateForSync(state)

      const itemIdsToFetch = getItemsToFetch(
        sortedTtsAudiosIds,
        previouslyGeneratedUnchangedItems
      )
      const newItems = await fetchTtsItemsByIds(itemIdsToFetch)
      const allGeneratedItemsData = [
        ...previouslyGeneratedUnchangedItems.map(i => i.data),
        ...newItems
      ]

      const shouldReSync = determineShouldReSync({
        allGeneratedItemsData,
        sortedTtsAudiosIds,
        newGeneratedItemsInitialIdAndStatus,
        newItems
      })

      if (!shouldReSync) return

      dispatch(setLoadingStates(true))

      // Reorder items according to timeline position and filter if it was deleted from the timeline
      const reorderedTtsItems = getFinalReorderedAndFilteredItems(
        allGeneratedItemsData,
        sortedTtsAudiosIds
      )

      // Merge reordered items with not generated or draft items, preserving user changes
      const finalTtsItems = mergeAllItems(
        reorderedTtsItems,
        notGeneratedOrDraftTtsItems,
        newGeneratedItemsInitialIdAndStatus
      )

      dispatch(actions.setInitialTtsItems(reorderedTtsItems))
      dispatch(actions.setTtsItems(finalTtsItems))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setNewGeneratedItemsInitialIdAndStatus([]))
      dispatch(setLoadingStates(false))
    }
  }

//// *********** better call Saul *********** ////

const updateTtsItemGenerationStatus =
  (id: number, isDataChanged: boolean) =>
  async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const isGenerated = ttsSelectors.makeTtsItemGeneratedStateById()(state, id)
    if (isGenerated && isDataChanged) {
      dispatch(actions.updateTtsGeneratedState({ id, value: false }))
    }
    if (!isGenerated && !isDataChanged) {
      dispatch(actions.updateTtsGeneratedState({ id, value: true }))
    }
  }

const checkGeneratedStateByText =
  (id: number, newText: string) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const isItemDataAlreadyChanged =
      ttsSelectors.makeTtsItemOtherKeysAlreadyChanged(
        TTS_ITEM_DATA_CHANGEABLE_KEYS.text
      )(state, id)

    if (isItemDataAlreadyChanged) {
      return
    }

    const currentText = ttsSelectors.makeInitialTtsItemTextById()(state, id)
    const isTextChanged = getIsTextChanged(currentText, newText)
    dispatch(updateTtsItemGenerationStatus(id, isTextChanged))
  }

const checkGeneratedStateBySpeakerId =
  (id: number, newSpeakerId: number) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()

    const isItemDataAlreadyChanged =
      ttsSelectors.makeTtsItemOtherKeysAlreadyChanged(
        TTS_ITEM_DATA_CHANGEABLE_KEYS.speakerId
      )(state, id)

    if (isItemDataAlreadyChanged) {
      return
    }

    const currentSpeakerId = ttsSelectors.makeInitialTtsItemSpeakerIdById()(
      state,
      id
    )
    const isSpeakerIdChanged = currentSpeakerId !== newSpeakerId

    dispatch(updateTtsItemGenerationStatus(id, isSpeakerIdChanged))
  }

const checkGeneratedStateByLangCode =
  (id: number, newLangCode: string) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()

    const isItemDataAlreadyChanged =
      ttsSelectors.makeTtsItemOtherKeysAlreadyChanged(
        TTS_ITEM_DATA_CHANGEABLE_KEYS.langCode
      )(state, id)

    if (isItemDataAlreadyChanged) {
      return
    }

    const currentLangCode = ttsSelectors.makeInitialTtsItemLangCodeById()(
      state,
      id
    )
    const isLangCodeChanged = currentLangCode !== newLangCode

    dispatch(updateTtsItemGenerationStatus(id, isLangCodeChanged))
  }

const checkGeneratedStateBySpeed =
  (id: number, newSpeed: number) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()

    const isItemDataAlreadyChanged =
      ttsSelectors.makeTtsItemOtherKeysAlreadyChanged(
        TTS_ITEM_DATA_CHANGEABLE_KEYS.speed
      )(state, id)

    if (isItemDataAlreadyChanged) {
      return
    }

    const currentSpeed = ttsSelectors.makeInitialTtsItemSpeedById()(state, id)
    const isSpeedChanged = currentSpeed !== newSpeed

    dispatch(updateTtsItemGenerationStatus(id, isSpeedChanged))
  }

const checkGeneratedStateByInstruction =
  (id: number, newInstruction: string) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()

    const isItemDataAlreadyChanged =
      ttsSelectors.makeTtsItemOtherKeysAlreadyChanged(
        TTS_ITEM_DATA_CHANGEABLE_KEYS.instruction
      )(state, id)

    if (isItemDataAlreadyChanged) {
      return
    }

    const currentInstruction = ttsSelectors.makeInitialTtsItemInstructionById()(
      state,
      id
    )
    const isInstructionChanged = currentInstruction !== newInstruction

    dispatch(updateTtsItemGenerationStatus(id, isInstructionChanged))
  }

// to change the tts item data, we need to check if the data has been already changed by other keys
// to not change the generated state once more
const setTtsItemText =
  (id: number, value: string) => (dispatch: TAppDispatch) => {
    dispatch(checkGeneratedStateByText(id, value))
    dispatch(actions.updateTtsItemDataText({ id, value }))
  }

const setTtsItemSpeakerId =
  (id: number, value: number) => (dispatch: TAppDispatch) => {
    dispatch(checkGeneratedStateBySpeakerId(id, value))
    dispatch(actions.updateTtsItemDataSpeakerId({ id, value }))
  }

const setTtsItemLangCode =
  (id: number, value: string) => (dispatch: TAppDispatch) => {
    dispatch(checkGeneratedStateByLangCode(id, value))
    dispatch(actions.updateTtsItemDataLangCode({ id, value }))
  }

const setTtsItemSpeed =
  (id: number, value: number) => (dispatch: TAppDispatch) => {
    dispatch(checkGeneratedStateBySpeed(id, value))
    dispatch(actions.updateTtsItemDataSpeed({ id, value }))
  }

const setTtsItemInstruction =
  (id: number, value: string) => (dispatch: TAppDispatch) => {
    dispatch(checkGeneratedStateByInstruction(id, value))
    dispatch(actions.updateTtsItemDataInstruction({ id, value }))
  }

// When bulk updating TTS item speaker IDs, we must check if any speaker ID
// has reverted to its original value (indicating it was already generated).
// Since generation state is stored in project data, this validation
// must be performed here to maintain data integrity.

const setTtsBulkSpeakerId =
  (speakerId: number) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()

    const { speed, langCode, instructions } = ttsSelectors.ttsBulkValues(state)
    const ttsItemsWithUnchangedSpeakerIds = ttsSelectors.ttsUnchangedItems(
      state,
      {
        speakerId,
        speed,
        langCode,
        instructions
      }
    )

    dispatch(
      actions.bulkUpdateTtsItemsSpeakerId({
        value: speakerId,
        unchangedItems: ttsItemsWithUnchangedSpeakerIds
      })
    )
  }

const setTtsBulkLangCode =
  (langCode: string) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()

    const { speed, speakerId, instructions } = ttsSelectors.ttsBulkValues(state)

    const ttsItemsWithUnchangedLangCodes = ttsSelectors.ttsUnchangedItems(
      state,
      {
        langCode,
        speed,
        speakerId,
        instructions
      }
    )

    dispatch(
      actions.bulkUpdateTtsItemsLangCode({
        value: langCode,
        unchangedItems: ttsItemsWithUnchangedLangCodes
      })
    )

    const onSetBulkSpeakerCallback = (
      speakerId: number,
      unchangedItems: number[]
    ) => {
      dispatch(
        actions.bulkUpdateTtsItemsSpeakerId({
          value: speakerId,
          unchangedItems
        })
      )
    }
    dispatch(getSpeakersPerLangCode(langCode, onSetBulkSpeakerCallback))
  }

const setTtsBulkSpeed =
  (speed: number) => (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const { speakerId, langCode, instructions } =
      ttsSelectors.ttsBulkValues(state)
    const ttsItemsWithUnchangedSpeed = ttsSelectors.ttsUnchangedItems(state, {
      speakerId,
      speed,
      langCode,
      instructions
    })

    dispatch(
      actions.bulkUpdateTtsItemsSpeed({
        value: speed,
        unchangedItems: ttsItemsWithUnchangedSpeed
      })
    )
  }

const setTtsBulkInstruction =
  (instructions: string) =>
  (dispatch: TAppDispatch, getState: () => TRootState) => {
    const state = getState()
    const { speakerId, langCode, speed } = ttsSelectors.ttsBulkValues(state)

    const ttsItemsWithUnchangedInstruction = ttsSelectors.ttsUnchangedItems(
      state,
      {
        speakerId,
        langCode,
        speed,
        instructions
      }
    )

    dispatch(
      actions.bulkUpdateTtsItemsInstruction({
        value: instructions,
        unchangedItems: ttsItemsWithUnchangedInstruction
      })
    )
  }

const handleTtsGenerationError =
  (errorCode: string | undefined) => (dispatch: TAppDispatch) => {
    if (errorCode === 'TTS_LANGUAGE_MISMATCH_ERROR') {
      dispatch(actions.setIsTtsMismatchPopupVisible(true))
    } else {
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }

    dispatch(actions.setTtsGenerationStatus(TTS_GENERATION_STATUS.error))
  }

export const ttsThunks = {
  ...actions,
  syncTtsAudios,
  getSpeakerTones,
  getLanguageOptions,
  addNewTtsDraftBlock,
  getSpeakersPerLangCode,
  setTtsInitialDraftItemData,
  setTtsItemText,
  setTtsItemSpeakerId,
  setTtsItemLangCode,
  setTtsItemSpeed,
  setTtsItemInstruction,
  updateTtsItemGenerationStatus,
  checkGeneratedStateByText,
  checkGeneratedStateBySpeakerId,
  checkGeneratedStateByLangCode,
  checkGeneratedStateBySpeed,
  checkGeneratedStateByInstruction,
  setTtsBulkSpeakerId,
  setTtsBulkLangCode,
  setTtsBulkSpeed,
  setTtsBulkInstruction,
  getUserTtsCredits,
  generateTtsItems
}
